import { useState } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    guests: 1,
  });
  const [checkIn, setCheckIn] = useState(undefined);
  const [checkOut, setCheckOut] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showCheckOut, setShowCheckOut] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        check_in: checkIn ? format(checkIn, "yyyy-MM-dd") : "",
        check_out: checkOut ? format(checkOut, "yyyy-MM-dd") : "",
      });
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "", guests: 1 });
      setCheckIn(undefined);
      setCheckOut(undefined);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <p className="font-marker text-teal text-sm">Get in touch</p>
          <h1 className="mt-2 font-anton text-4xl tracking-wider text-sand stencil-text sm:text-6xl">
            BOOK YOUR STAY
          </h1>
          <div className="mt-2 h-1 w-20 bg-coral" />
          <p className="mt-4 font-courier text-sm text-sand/70 max-w-lg">
            Ready to seas the day? Fill out the form below and we'll get back to you
            faster than you can say "beachfront breakfast."
          </p>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-3">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-anton text-xl tracking-wider text-ink stencil-text">
                FIND US
              </h2>
              <div className="mt-2 h-1 w-16 bg-teal" />

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-coral" />
                  <div>
                    <p className="font-outfit text-sm font-medium text-ink">Address</p>
                    <p className="font-courier text-sm text-ink/70">
                      123 Ocean Avenue<br />
                      Daytona Beach, FL 32118
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="mt-1 h-5 w-5 flex-shrink-0 text-coral" />
                  <div>
                    <p className="font-outfit text-sm font-medium text-ink">Phone</p>
                    <p className="font-courier text-sm text-ink/70">(320) 468-6277</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="mt-1 h-5 w-5 flex-shrink-0 text-coral" />
                  <div>
                    <p className="font-outfit text-sm font-medium text-ink">Email</p>
                    <p className="font-courier text-sm text-ink/70">stay@seasthebay.org</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="mt-1 h-5 w-5 flex-shrink-0 text-coral" />
                  <div>
                    <p className="font-outfit text-sm font-medium text-ink">Check-in / Check-out</p>
                    <p className="font-courier text-sm text-ink/70">
                      Check-in: 3:00 PM<br />
                      Check-out: 11:00 AM
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 card-brutal bg-white p-6">
                <p className="font-marker text-coral text-sm">Pro tip</p>
                <p className="mt-2 font-courier text-xs text-ink/70 leading-relaxed">
                  Book directly with us and skip the booking platform fees.
                  We're real people. We answer emails. Sometimes even phone calls.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="card-brutal bg-white p-8 lg:p-12"
              >
                <h2 className="font-anton text-xl tracking-wider text-ink stencil-text">
                  SEND US A MESSAGE
                </h2>
                <div className="mt-2 h-1 w-16 bg-coral" />

                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-outfit text-sm uppercase tracking-wider text-ink">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="border-2 border-ink/20 bg-sand font-courier text-sm focus:border-teal rounded-none"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-outfit text-sm uppercase tracking-wider text-ink">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@email.com"
                      className="border-2 border-ink/20 bg-sand font-courier text-sm focus:border-teal rounded-none"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-outfit text-sm uppercase tracking-wider text-ink">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(320) 468-6277"
                      className="border-2 border-ink/20 bg-sand font-courier text-sm focus:border-teal rounded-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guests" className="font-outfit text-sm uppercase tracking-wider text-ink">
                      Number of Guests
                    </Label>
                    <Input
                      id="guests"
                      name="guests"
                      type="number"
                      min="1"
                      max="10"
                      data-testid="contact-guests-input"
                      value={form.guests}
                      onChange={handleChange}
                      className="border-2 border-ink/20 bg-sand font-courier text-sm focus:border-teal rounded-none"
                    />
                  </div>
                </div>

                {/* Date Pickers */}
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-outfit text-sm uppercase tracking-wider text-ink">
                      Check-in Date
                    </Label>
                    <button
                      type="button"
                      data-testid="contact-checkin-trigger"
                      onClick={() => setShowCheckIn(!showCheckIn)}
                      className="w-full border-2 border-ink/20 bg-sand px-3 py-2 text-left font-courier text-sm text-ink/70 hover:border-teal transition-colors"
                    >
                      {checkIn ? format(checkIn, "MMM dd, yyyy") : "Select check-in date"}
                    </button>
                    {showCheckIn && (
                      <div className="border-2 border-ink bg-white shadow-brutal p-2">
                        <Calendar
                          mode="single"
                          selected={checkIn}
                          onSelect={(date) => {
                            setCheckIn(date);
                            setShowCheckIn(false);
                          }}
                          disabled={(date) => date < new Date()}
                          data-testid="contact-checkin-calendar"
                          className="font-courier"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="font-outfit text-sm uppercase tracking-wider text-ink">
                      Check-out Date
                    </Label>
                    <button
                      type="button"
                      data-testid="contact-checkout-trigger"
                      onClick={() => setShowCheckOut(!showCheckOut)}
                      className="w-full border-2 border-ink/20 bg-sand px-3 py-2 text-left font-courier text-sm text-ink/70 hover:border-teal transition-colors"
                    >
                      {checkOut ? format(checkOut, "MMM dd, yyyy") : "Select check-out date"}
                    </button>
                    {showCheckOut && (
                      <div className="border-2 border-ink bg-white shadow-brutal p-2">
                        <Calendar
                          mode="single"
                          selected={checkOut}
                          onSelect={(date) => {
                            setCheckOut(date);
                            setShowCheckOut(false);
                          }}
                          disabled={(date) => date < (checkIn || new Date())}
                          data-testid="contact-checkout-calendar"
                          className="font-courier"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <Label htmlFor="message" className="font-outfit text-sm uppercase tracking-wider text-ink">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your trip — room preferences, special occasions, questions..."
                    rows={5}
                    className="border-2 border-ink/20 bg-sand font-courier text-sm focus:border-teal rounded-none resize-none"
                    required
                  />
                </div>

                <p data-testid="contact-sms-consent" className="mt-6 font-courier text-xs text-ink/60 leading-relaxed">
                  By entering your phone number and tapping Continue, you consent to receive a one-time verification code via SMS. Message and data rates may apply. Your phone number will only be used for authentication and order updates. View our{" "}
                  <a href="/privacy" className="text-teal underline hover:text-coral transition-colors">Privacy Policy</a>{" "}and{" "}
                  <a href="/terms" className="text-teal underline hover:text-coral transition-colors">Terms of Service</a>.
                </p>

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="mt-6 bg-ink text-sand font-anton tracking-widest text-sm px-10 py-6 hover:-translate-y-1 hover:shadow-brutal-teal transition-all duration-200 rounded-none disabled:opacity-50"
                >
                  {loading ? "SENDING..." : "SEND MESSAGE"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
